import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import BaseModal from '../../components/common/BaseModal/BaseModal';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { DropdownIndicator } from '../../components/common/SelectComponents';
import { formatOptionLabel, getSelectStyles } from '../../utils/selectStyles';
import { connectionService, repositoryService } from '../../services/apiService';
import API_ENDPOINTS from '../../config/apiConfig';
import { prepareRepositoryPat } from '../../utils/encrypt';
import './AddRepositoryModal.css';

// ─── Initial state ─────────────────────────────────────────────────────────────

const initialFormData = {
    connectionName: '',
    pat: '',
    username: '',
    repository: '',
    branch: '',
    comments: '',
};

// ─── Character limits ──────────────────────────────────────────────────────────

const CHARACTER_LIMITS = {
    connectionName: 100,
    username: 40,
    comments: 50,
};

// ─── Component ─────────────────────────────────────────────────────────────────

const AddRepositoryModal = ({ isOpen, onClose, onSuccess, setIsLoading }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});

    const [serverPublicKey, setServerPublicKey] = useState(null);

    const [showPat, setShowPat] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isTestSuccessful, setIsTestSuccessful] = useState(false);

    // Post-test data
    const [repositories, setRepositories] = useState([]);       // [{ value, label }]
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);

    // Adding
    const [isAdding, setIsAdding] = useState(false);

    const modalBodyRef = useRef(null);

    // ─── Derived ───────────────────────────────────────────────────────────────

    // PAT + Username fields are locked once test succeeds
    const credentialsLocked = isTestSuccessful;

    // "Add Connection" is only active after test + required post-test fields filled
    const canAdd = isTestSuccessful && formData.repository && formData.branch;

    // Fetch server public key on component mount
    useEffect(() => {
        const fetchPublicKey = async () => {
            try {
                const publicKey = await connectionService.getPublicKey();
                setServerPublicKey(publicKey);
            } catch (err) {
                console.error("Failed to fetch server public key:", err);
            }
        };

        fetchPublicKey();
    }, []);


    // ─── Handlers ──────────────────────────────────────────────────────────────

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

        // Any edit to credentials before test resets the tested state
        if ((field === 'pat' || field === 'username') && isTestSuccessful) {
            setIsTestSuccessful(false);
            setRepositories([]);
            setBranches([]);
            setFormData(prev => ({ ...prev, [field]: value, repository: '', branch: '' }));
        }
    };

    const handleRepositoryChange = async (opt) => {
        const value = opt ? opt.value : '';
        setFormData(prev => ({ ...prev, repository: value, branch: '' }));
        setBranches([]);
        if (errors.repository) setErrors(prev => { const n = { ...prev }; delete n.repository; return n; });

        if (value) {
            setIsLoading(true);
            try {
                const encryptedPat = await prepareRepositoryPat(formData.pat, serverPublicKey);
                const result = await repositoryService.getBranchValues({
                    repository: value,
                    ...encryptedPat,           // pat: { iv, ciphertext }, client_pub, salt
                });
                // Normalise: API may return { branches: [...] } or a plain array
                const raw = Array.isArray(result) ? result : (result?.branches ?? []);
                setBranches(raw.map(b => ({ value: b.name, label: b.name })));
            } catch {
                toast.error('Failed to load branches');
                setBranches([]);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleBranchChange = (opt) => {
        setFormData(prev => ({ ...prev, branch: opt ? opt.value : '' }));
        if (errors.branch) setErrors(prev => { const n = { ...prev }; delete n.branch; return n; });
    };

    // ─── Validation helpers ────────────────────────────────────────────────────

    const validatePreTest = () => {
        const errs = {};
        if (!formData.pat.trim()) errs.pat = 'PAT is required';
        if (!formData.username.trim()) errs.username = 'Username is required';
        else if (formData.username.length > CHARACTER_LIMITS.username)
            errs.username = `Username exceeds the ${CHARACTER_LIMITS.username} character limit`;
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateFull = () => {
        const errs = {};
        if (!formData.connectionName.trim()) errs.connectionName = 'Connection Name is required';
        else if (formData.connectionName.length > CHARACTER_LIMITS.connectionName)
            errs.connectionName = `Connection Name exceeds the ${CHARACTER_LIMITS.connectionName} character limit`;
        if (!formData.pat.trim()) errs.pat = 'PAT is required';
        if (!formData.username.trim()) errs.username = 'Username is required';
        else if (formData.username.length > CHARACTER_LIMITS.username)
            errs.username = `Username exceeds the ${CHARACTER_LIMITS.username} character limit`;
        if (!formData.repository) errs.repository = 'Repository is required';
        if (!formData.branch) errs.branch = 'Branch is required';
        if (formData.comments.length > CHARACTER_LIMITS.comments)
            errs.comments = `Comments exceed the ${CHARACTER_LIMITS.comments} character limit`;
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ─── Test Connection ───────────────────────────────────────────────────────

    const handleTestConnection = async () => {
        if (!validatePreTest()) return;
        if (!serverPublicKey) {
            toast.error('Encryption key not loaded yet. Please try again.');
            return;
        }

        setIsLoading(true);
        try {
            const encryptedPat = await prepareRepositoryPat(formData.pat, serverPublicKey);
            const result = await repositoryService.getRepoValues({
                github_username: formData.username,
                ...encryptedPat,               // pat: { iv, ciphertext }, client_pub, salt
            });

            // Normalise repositories list — API returns [{ name, full_name }]
            const raw = Array.isArray(result) ? result : (result?.repositories ?? []);
            const repoOptions = raw.map(r => ({
                value: r.full_name,
                label: r.full_name,
            }));

            setRepositories(repoOptions);
            setIsTestSuccessful(true);
            toast.success('Connection tested successfully');
        } catch (error) {
            setIsTestSuccessful(false);
            toast.error(error.message || 'Test connection failed. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Add Connection ────────────────────────────────────────────────────────

    const handleAddConnection = async () => {
        if (!validateFull()) return;
        if (!serverPublicKey) {
            toast.error('Encryption key not loaded yet. Please try again.');
            return;
        }

        setIsLoading(true);
        try {
            const encryptedPat = await prepareRepositoryPat(formData.pat, serverPublicKey);
            const payload = {
                name: formData.connectionName,
                github_username: formData.username,
                repository: formData.repository,
                repository_full_name: formData.repository,
                branch: formData.branch,
                comment: formData.comments || null,
                ...encryptedPat,      // pat: { iv, ciphertext }, client_pub, salt
            };
            await repositoryService.addRepository(payload);
            toast.success('Repository added successfully');
            await onSuccess?.();
            handleClose();
        } catch (error) {
            toast.error(error.message || 'Failed to add repository connection. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Close / reset ─────────────────────────────────────────────────────────

    const handleClose = () => {
        setFormData(initialFormData);
        setErrors({});
        setShowPat(false);
        setIsTestSuccessful(false);
        setRepositories([]);
        setBranches([]);
        onClose();
    };

    // ─── Footer ────────────────────────────────────────────────────────────────

    const footer = (
        <>
            <button type="button" className="btn btn-cancel" onClick={handleClose}>
                Cancel
            </button>
            <button
                type="button"
                className="btn btn-secondary"
                onClick={handleTestConnection}
            // disabled={credentialsLocked}
            >
                Test Connection
            </button>
            <button
                type="button"
                className={"btn btn-primary"}
                onClick={handleAddConnection}
                // disabled={isAdding || isTesting}
                disabled={!isTestSuccessful}
            >
                Add Connection
            </button>
        </>
    );

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add Repository"
            footer={footer}
            className="add-repository-modal"
            modalBodyRef={modalBodyRef}
        >
            <div className="add-repository-form">

                {/* Connection Name */}
                <div className="form-row">
                    <div className="form-group form-group-full">
                        <label>
                            Connection Name <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., my-gitHub-prod"
                            autoComplete="off"
                            value={formData.connectionName}
                            onChange={(e) => handleChange('connectionName', e.target.value)}
                            className={errors.connectionName ? 'error' : ''}
                        />
                        <ErrorMessage message={errors.connectionName} />
                    </div>
                </div>

                {/* PAT + Username */}
                <div className="form-row">
                    <div className="form-group">
                        <label>
                            PAT <span className="required">*</span>
                        </label>
                        <div className="repo-input-with-toggle">
                            <input
                                type={showPat ? 'text' : 'password'}
                                placeholder="Enter Personal Access Token"
                                autoComplete="new-password"
                                value={formData.pat}
                                onChange={(e) => handleChange('pat', e.target.value)}
                                className={errors.pat ? 'error' : ''}
                            // disabled={credentialsLocked}
                            />
                            <button
                                type="button"
                                className="repo-eye-btn"
                                onClick={() => setShowPat(v => !v)}
                                tabIndex={-1}
                            // disabled={credentialsLocked}
                            >
                                {showPat
                                    ? <Eye size="0.833vw" style={{ width: '0.833vw', height: '0.833vw' }} />
                                    : <EyeOff size="0.833vw" style={{ width: '0.833vw', height: '0.833vw' }} />
                                }
                            </button>
                        </div>
                        <ErrorMessage message={errors.pat} />
                    </div>

                    <div className="form-group">
                        <label>
                            Username <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., john.doe"
                            autoComplete="off"
                            value={formData.username}
                            onChange={(e) => handleChange('username', e.target.value)}
                            className={errors.username ? 'error' : ''}
                        // disabled={credentialsLocked}
                        />
                        <ErrorMessage message={errors.username} />
                    </div>
                </div>

                {/* Post-test fields: Repository, Branch, Comments */}
                {isTestSuccessful && (
                    <>
                        {/* Repository + Branch */}
                        <div className="form-row">
                            <div className="form-group">
                                <label>
                                    Repository <span className="required">*</span>
                                </label>
                                <Select
                                    value={repositories.find(o => o.value === formData.repository) || null}
                                    onChange={handleRepositoryChange}
                                    options={repositories}
                                    placeholder="Select Repository"
                                    isSearchable
                                    isClearable
                                    formatOptionLabel={formatOptionLabel}
                                    styles={getSelectStyles(!!errors.repository)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                                    noOptionsMessage={() => 'No repositories found'}
                                    components={{ DropdownIndicator }}
                                />
                                <ErrorMessage message={errors.repository} />
                            </div>

                            <div className="form-group">
                                <label>
                                    Branch <span className="required">*</span>
                                </label>
                                <Select
                                    value={branches.find(o => o.value === formData.branch) || null}
                                    onChange={handleBranchChange}
                                    options={branches}
                                    placeholder="Select Branch"
                                    isSearchable
                                    isClearable
                                    // isDisabled={!isTestSuccessful || !formData.repository}
                                    // isLoading={loadingBranches}
                                    formatOptionLabel={formatOptionLabel}
                                    styles={getSelectStyles(!!errors.branch)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                                    noOptionsMessage={() => 'No branches found'}
                                    components={{ DropdownIndicator }}
                                />
                                <ErrorMessage message={errors.branch} />
                            </div>
                        </div>

                        {/* Comments */}
                        <div className="form-row">
                            <div className="form-group form-group-full">
                                <label>Comments</label>
                                <input
                                    type='text'
                                    placeholder="Your comments here"
                                    value={formData.comments}
                                    onChange={(e) => handleChange('comments', e.target.value)}
                                    className={errors.comments ? 'error' : ''}
                                />
                                <ErrorMessage message={errors.comments} />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </BaseModal>
    );
};

export default AddRepositoryModal;